import { Box } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import type React from 'react';
import styled, { keyframes } from 'styled-components';

const BREATH_DURATION: Milliseconds = 5000;
const BASE_TEXTURE_SIZE = 6000;
const MEDIUM_TEXTURE_SIZE = 5000;
const SMALL_TEXTURE_SIZE = 4000;
const TINY_TEXTURE_SIZE = 3300; // because silly firefox can't handle large rotating images :(

const KaleidoscopeContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: white;
  will-change: contents;

  ${MEDIA_QUERIES.mobile`
        --texture-multiplier: ${SMALL_TEXTURE_SIZE / BASE_TEXTURE_SIZE};
    `}

  ${MEDIA_QUERIES.tablet`
        --texture-multiplier: ${MEDIUM_TEXTURE_SIZE / BASE_TEXTURE_SIZE};
    `}

    ${MEDIA_QUERIES.desktop`
        --texture-multiplier: 1;
    `}

    @-moz-document url-prefix() {
    --texture-multiplier: ${TINY_TEXTURE_SIZE / BASE_TEXTURE_SIZE} !important;
  }

  /* config */
  --texture-size: ${BASE_TEXTURE_SIZE}px;
  --fluid-texture-size: 400vw;
  --ring-radius-ratio: 6;
  --minimum-scale: 0.5;

  /* computed */
  --ring-radius: calc(var(--texture-size) / var(--ring-radius-ratio));
  --slice-width: calc(0.4 * var(--texture-size)); /* large enough so texture is not truncated in wide screens */
  --slice-height: calc(0.45638 * var(--slice-width)); /* TODO: value needs to take # of slices into account */
  --temp-scaled-texture-size: min(
    var(--texture-size),
    max(calc(var(--texture-size) * var(--minimum-scale)), var(--fluid-texture-size))
  );
  --scaled-texture-size: calc(var(--texture-multiplier) * var(--temp-scaled-texture-size));
  --scaled-ring-radius: min(
    var(--ring-radius),
    max(calc(var(--ring-radius) * var(--minimum-scale)), calc(var(--fluid-texture-size) / var(--ring-radius-ratio)))
  );
  --texture-margin: calc(-1 * (var(--slice-width) + var(--scaled-ring-radius) * 2));
`;

const SliceContainer = styled.div`
  width: var(--slice-width);
  height: var(--slice-height);
  transform-origin: 0 50%;
  position: absolute;
  clip-path: polygon(-0.15% 50%, 100% 0%, 100% 100%);
  top: calc(50% - var(--slice-height) / 2);
  left: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const hypnoMove = keyframes`
    0% {
        transform-origin: 50% 50%;
    }
    100% {
        transform-origin: 49.9% 49.9%;
    }
`;

const Slice = styled(Box)<{ breathing: boolean }>`
  flex-grow: 1;
  background-size: contain;
  min-width: var(--scaled-texture-size);
  min-height: var(--scaled-texture-size);
  max-width: var(--scaled-texture-size);
  max-height: var(--scaled-texture-size);
  transform-origin: 50% 50%;
  background-position: 50% 50%;
  display: inline-block;
  background-repeat: no-repeat;
  margin-left: var(--texture-margin);
  animation: ${hypnoMove} ${BREATH_DURATION}ms alternate-reverse infinite ease-in-out;
`;

interface Props {
  slices: number;
  imagesUrls: string[];
  sliceTransform: (t: number) => string;
  t: number;
  breathing: boolean;
}

const Kaleidoscope: React.FC<Props> = ({ t, slices, sliceTransform, imagesUrls, breathing }) => {
  return (
    <KaleidoscopeContainer>
      {Array(slices)
        .fill(null)
        .map((_, i) => {
          const containerRotateStyle = `rotate(${(i / slices) * Math.PI * 2}rad)`;
          const containerScaleStyle = `scaleY(${i % 2 === 0 ? 1 : -1})`;
          const containerTransformStyle = {
            transform: `${containerRotateStyle} ${containerScaleStyle}`,
          };

          const sliceTransformStyle = {
            transform: sliceTransform(t),
            animationPlayState: breathing ? 'running' : 'paused',
          };

          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <SliceContainer style={containerTransformStyle} key={i}>
              <Slice
                breathing={breathing}
                style={sliceTransformStyle}
                backgroundImage={imagesUrls.map(url => `url(${url})`)}
              />
            </SliceContainer>
          );
        })}
    </KaleidoscopeContainer>
  );
};

export default Kaleidoscope;
